import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { canManageAssignments } from '../lib/assetPermissions';
import { assignedNames } from './AssetAssignmentCell';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Modal disparado al mover un activo a la fase "Mediación".
 *
 * Requisitos (spec Feb 2026):
 *  - Selección obligatoria de al menos 1 mediador (multi-select) de entre
 *    TODOS los usuarios registrados de la plataforma.
 *  - Checkbox opcional "Pendiente de mediador" → al confirmar, marca el
 *    activo como pendiente de revisión y notifica a los admins/superadmins
 *    asignados con una nota automática.
 *
 * Props:
 *  - open: boolean.
 *  - asset: { id, codigo_npl, mediadores }.
 *  - onClose(): void.
 *  - onConfirmed(updated): callback tras guardar OK (recibe el activo tras patch).
 */
const MediacionAssignModal = ({ open, asset, onClose, onConfirmed }) => {
  const { user } = useAuth();
  const canAssign = canManageAssignments(user);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [pendiente, setPendiente] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(asset?.mediadores || []);
    setPendiente(false);
    if (!canAssign) { setUsers([]); setLoading(false); return; }
    setLoading(true);
    axios
      .get(`${API}/users`, { withCredentials: true })
      .then((r) => setUsers(r.data?.items ?? r.data ?? []))
      .catch(() => toast.error('No se pudo cargar la lista de usuarios'))
      .finally(() => setLoading(false));
  }, [open, asset?.id, canAssign]);

  if (!open) return null;

  const toggleUser = (uid) => {
    if (!canAssign) return;
    setSelected((prev) => (prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]));
  };

  // (Feb 2026) Se permite avanzar si el usuario asigna al menos un mediador
  // O marca "Pendiente de mediador". No se puede confirmar si no marca nada.
  const canSubmit = (selected.length > 0 || pendiente) && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // 1) Persistir mediadores + etapa_visual='mediacion'.
      const patch = await axios.patch(
        `${API}/assets/${asset.id}/quick-update`,
        { etapa_visual: 'mediacion', ...(canAssign ? { mediadores: selected } : {}) },
        { withCredentials: true },
      );
      const updated = patch.data?.asset || null;

      // 2) Si marcado "Pendiente de mediador" → toggle-review a admins asignados.
      if (pendiente) {
        try {
          const recRes = await axios.get(`${API}/assets/${asset.id}/review-recipients`, {
            withCredentials: true,
          });
          const groups = recRes.data?.groups || [];
          const admins = (groups.find((g) => g.role === 'admin')?.users || []).map((u) => u.id);
          if (admins.length === 0) {
            toast.warning('Activo marcado, pero no hay admins asignados para notificar');
          }
          await axios.post(
            `${API}/assets/${asset.id}/toggle-review`,
            {
              marked: true,
              recipient_ids: admins,
              note: 'Mediador pendiente de asignar / revisar (auto)',
            },
            { withCredentials: true },
          );
        } catch (e) {
          // No abortamos: el cambio de fase + asignación ya se hizo.
          toast.error('Fase actualizada, pero no se pudo enviar la revisión: ' + (e.response?.data?.detail || e.message));
        }
      }

      toast.success('Fase Mediación asignada' + (pendiente ? ' · pendiente de revisión notificado' : ''));
      onConfirmed?.(updated);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
      data-testid="mediacion-modal-overlay"
    >
      <div
        className="rounded-xl shadow-2xl max-w-xl w-full max-h-[85vh] flex flex-col"
        style={{ background: 'var(--color-bg-elevated)' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="mediacion-modal"
      >
        <div className="flex items-start justify-between p-5 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-blue-500 text-white">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-base" style={{ color: 'var(--color-text-primary)' }}>
                Pasar a fase Mediación
              </h3>
              <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                {canAssign ? 'Asigna al menos un mediador antes de continuar' : 'Las asignaciones actuales se conservarán'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-700/20 rounded"
            aria-label="Cerrar"
            data-testid="mediacion-modal-close"
          >
            <X className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-4 space-y-4">
          <div>
            <p className="text-xs uppercase tracking-wide font-semibold mb-2" style={{ color: 'var(--color-text-muted)' }}>
              Mediadores <span className="text-red-500">*</span>
            </p>
            {!canAssign ? (
              <p className="text-sm [overflow-wrap:anywhere]" data-testid="mediacion-assignees-readonly" style={{ color: 'var(--color-text-primary)' }}>
                {assignedNames(asset || {}, 'mediadores').join(', ') || 'Sin mediadores asignados'}
              </p>
            ) : loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              </div>
            ) : (
              <div className="border rounded-lg p-2 max-h-64 overflow-y-auto space-y-1" style={{ borderColor: 'var(--color-border)' }}>
                {users.map((u) => (
                  <label
                    key={u.id}
                    className="flex items-center gap-2 text-sm cursor-pointer hover:opacity-90 px-1 py-0.5 rounded"
                    data-testid={`mediacion-user-${u.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(u.id)}
                      onChange={() => toggleUser(u.id)}
                      data-testid={`mediacion-user-checkbox-${u.id}`}
                      className="w-4 h-4 accent-blue-500"
                    />
                    <span style={{ color: 'var(--color-text-primary)' }}>{u.name || u.email}</span>
                    <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>({u.role})</span>
                  </label>
                ))}
                {users.length === 0 && (
                  <p className="text-sm italic p-2" style={{ color: 'var(--color-text-muted)' }}>
                    No hay usuarios disponibles.
                  </p>
                )}
              </div>
            )}
            {selected.length === 0 && !pendiente && !loading && (
              <p className="text-[11px] text-red-500 mt-1">
                {canAssign ? 'Selecciona al menos un mediador o marca ' : 'Para solicitar la asignación a un administrador, marca '}<em>Pendiente de mediador</em>.
              </p>
            )}
          </div>

          <label
            className="flex items-start gap-2 p-3 rounded-lg border cursor-pointer hover:opacity-90"
            style={{ borderColor: 'var(--color-border)' }}
            data-testid="mediacion-pendiente-wrapper"
          >
            <input
              type="checkbox"
              checked={pendiente}
              onChange={(e) => setPendiente(e.target.checked)}
              className="w-4 h-4 mt-0.5 accent-amber-500"
              data-testid="mediacion-pendiente-checkbox"
            />
            <div className="text-sm">
              <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                Pendiente de mediador
              </p>
              <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                Marca el activo como "Solicitada revisión" y notifica a los admins asignados.
              </p>
            </div>
          </label>
        </div>

        <div className="p-4 border-t flex items-center justify-end gap-2" style={{ borderColor: 'var(--color-border)' }}>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded border"
            style={{ color: 'var(--color-text-primary)', borderColor: 'var(--color-border)', background: 'transparent' }}
            data-testid="mediacion-modal-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`px-4 py-2 text-sm font-semibold rounded flex items-center gap-2 transition-colors ${
              canSubmit ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-slate-300 text-slate-500 cursor-not-allowed'
            }`}
            data-testid="mediacion-modal-submit"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Confirmar y pasar a Mediación
          </button>
        </div>
      </div>
    </div>
  );
};

export default MediacionAssignModal;
